import React, { useState, useEffect } from 'react';
import { spreadsheetApi } from '../services/spreadsheetApi';

const SpreadsheetPanel = ({ companyIdSlug, sessionId }) => {
  const [status, setStatus] = useState(null);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedSpreadsheet, setSelectedSpreadsheet] = useState('all');
  
  // New: Display mode and drawer state
  const [displayMode, setDisplayMode] = useState('compact'); // 'compact' | 'table' | 'grouped'
  const [selectedRow, setSelectedRow] = useState(null); // For detail drawer
  const [groupBy, setGroupBy] = useState('owner'); // 'owner' | 'item' | 'asset'
  
  // Load status on mount and poll every 10 seconds
  useEffect(() => {
    loadStatus();
    
    // Poll for updates every 10 seconds
    const interval = setInterval(() => {
      loadStatus();
    }, 10000);
    
    return () => clearInterval(interval);
  }, [companyIdSlug]);
  
  const loadStatus = async () => {
    try {
      const data = await spreadsheetApi.getStatus(companyIdSlug);
      setStatus(data);
    } catch (err) {
      console.error('Status load failed:', err);
    }
  };
  
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.name.endsWith('.xlsx')) {
      setError('Only .xlsx files supported');
      return;
    }
    
    setUploading(true);
    setError(null);
    
    try {
      const result = await spreadsheetApi.upload(file, companyIdSlug);
      await loadStatus();
      setError(null);
      alert(`Uploaded ${result.rows_imported} rows from ${result.tabs} tabs`);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };
  
  const handleQuery = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Include selectedSpreadsheet in query if not 'all'
      const payload = {
        query: query,
        company_id_slug: companyIdSlug,
        session_id: sessionId,
      };
      
      // If a specific spreadsheet is selected, add it to the payload
      if (selectedSpreadsheet && selectedSpreadsheet !== 'all') {
        payload.filename = selectedSpreadsheet;
      }
      
      const result = await spreadsheetApi.query(payload.query, companyIdSlug, sessionId, payload.filename);
      setResults(result);
    } catch (err) {
      setError(err.message);
      setResults(null);
    } finally {
      setLoading(false);
    }
  };
  
  // Preset query handler
  const runPresetQuery = (queryText) => {
    setQuery(queryText);
    setTimeout(() => {
      const form = document.querySelector('form');
      if (form) form.requestSubmit();
    }, 0);
  };
  
  const getStatusBadge = (status) => {
    const styles = {
      overdue: { bg: '#fee2e2', color: '#dc2626', label: 'Overdue', icon: '🔴' },
      due_soon: { bg: '#fef3c7', color: '#f59e0b', label: 'Due Soon', icon: '🟡' },
      ok: { bg: '#d1fae5', color: '#10b981', label: 'OK', icon: '🟢' },
      no_date: { bg: '#f3f4f6', color: '#6b7280', label: 'No Date', icon: '⚪' },
    };
    
    const style = styles[status] || styles.no_date;
    
    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '4px 10px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: '600',
        backgroundColor: style.bg,
        color: style.color,
      }}>
        {style.icon} {style.label}
      </span>
    );
  };
  
  // Group results by specified field
  const groupResults = (rows, groupByField) => {
    const grouped = {};
    rows.forEach(row => {
      const key = row[groupByField] || '(Unassigned)';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(row);
    });
    return grouped;
  };
  
  // Render compact list item (mobile-friendly)
  const renderCompactItem = (row, idx) => {
    const dateStr = row.event_date ? new Date(row.event_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';
    const daysText = row.days_until !== null 
      ? (row.days_until >= 0 ? `in ${row.days_until} days` : `${Math.abs(row.days_until)} days ago`)
      : '—';
    
    return (
      <div 
        key={idx} 
        onClick={() => setSelectedRow(row)}
        style={{
          padding: '16px',
          backgroundColor: 'white',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          marginBottom: '12px',
          cursor: 'pointer',
          transition: 'all 0.2s',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = '#4ECDC4';
          e.currentTarget.style.boxShadow = '0 2px 8px rgba(78, 205, 196, 0.15)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = '#e5e7eb';
          e.currentTarget.style.boxShadow = 'none';
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: '600', fontSize: '14px', color: '#111827', marginBottom: '4px' }}>
              {row.owner || 'Unassigned'} • {row.item || 'No item'}
            </div>
            <div style={{ fontSize: '13px', color: '#6b7280' }}>
              {dateStr} • {daysText}
            </div>
            {row.asset_unit && (
              <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                📍 {row.asset_unit}
              </div>
            )}
          </div>
          <div>
            {getStatusBadge(row.status)}
          </div>
        </div>
      </div>
    );
  };
  
  // Render row detail drawer
  const renderDrawer = () => {
    if (!selectedRow) return null;
    
    const dateStr = selectedRow.event_date ? new Date(selectedRow.event_date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : 'No date';
    const daysText = selectedRow.days_until !== null 
      ? (selectedRow.days_until >= 0 ? `Due in ${selectedRow.days_until} days` : `${Math.abs(selectedRow.days_until)} days overdue`)
      : 'No deadline';
    
    return (
      <>
        {/* Backdrop */}
        <div 
          onClick={() => setSelectedRow(null)}
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1000,
            animation: 'fadeIn 0.2s',
          }}
        />
        
        {/* Drawer - Bottom sheet on mobile, sidebar on desktop */}
        <div 
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            backgroundColor: 'white',
            borderRadius: '16px 16px 0 0',
            zIndex: 1001,
            maxHeight: '80vh',
            overflowY: 'auto',
            animation: 'slideUp 0.3s ease-out',
            boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.15)',
          }}
          className="ov-drawer-mobile"
        >
          <div style={{ padding: '24px' }}>
            {/* Handle bar */}
            <div style={{
              width: '40px',
              height: '4px',
              backgroundColor: '#e5e7eb',
              borderRadius: '2px',
              margin: '0 auto 20px',
            }} />
            
            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px' }}>
              <div>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                  {selectedRow.owner || 'Unassigned'}
                </h3>
                <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
                  {selectedRow.item || 'No item specified'}
                </p>
              </div>
              <button 
                onClick={() => setSelectedRow(null)}
                style={{
                  backgroundColor: '#f3f4f6',
                  border: 'none',
                  borderRadius: '8px',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: 'pointer',
                  fontSize: '20px',
                  color: '#6b7280',
                }}
              >
                ×
              </button>
            </div>
            
            {/* Status */}
            <div style={{ marginBottom: '24px' }}>
              {getStatusBadge(selectedRow.status)}
            </div>
            
            {/* Details */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              <div>
                <div style={{ fontSize: '12px', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', marginBottom: '4px' }}>
                  Due Date
                </div>
                <div style={{ fontSize: '14px', color: '#374151' }}>
                  {dateStr}
                </div>
                <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>
                  {daysText}
                </div>
              </div>
              
              {selectedRow.asset_unit && (
                <div>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', marginBottom: '4px' }}>
                    Asset / Unit
                  </div>
                  <div style={{ fontSize: '14px', color: '#374151' }}>
                    {selectedRow.asset_unit}
                  </div>
                </div>
              )}
              
              {selectedRow.event_type && (
                <div>
                  <div style={{ fontSize: '12px', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', marginBottom: '4px' }}>
                    Type
                  </div>
                  <div style={{ fontSize: '14px', color: '#374151' }}>
                    {selectedRow.event_type}
                  </div>
                </div>
              )}
              
              <div>
                <div style={{ fontSize: '12px', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', marginBottom: '4px' }}>
                  Source
                </div>
                <div style={{ fontSize: '14px', color: '#374151' }}>
                  📄 {selectedRow.source_file || 'Unknown file'}
                </div>
                {selectedRow.tab_name && (
                  <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>
                    Tab: {selectedRow.tab_name}
                  </div>
                )}
              </div>
              
              {/* Copy button */}
              <button
                onClick={() => {
                  const text = `Owner: ${selectedRow.owner || 'N/A'}\nItem: ${selectedRow.item || 'N/A'}\nDue: ${dateStr}\nAsset: ${selectedRow.asset_unit || 'N/A'}\nSource: ${selectedRow.source_file || 'N/A'}`;
                  navigator.clipboard.writeText(text);
                  alert('Copied to clipboard!');
                }}
                style={{
                  padding: '12px 16px',
                  backgroundColor: '#f3f4f6',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  color: '#374151',
                  cursor: 'pointer',
                  marginTop: '8px',
                }}
              >
                📋 Copy Details
              </button>
            </div>
          </div>
        </div>
      </>
    );
  };
  
  return (
    <div style={{ padding: '20px', fontFamily: 'system-ui, sans-serif', height: '100%', overflow: 'auto' }}>
      {/* Header */}
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap', gap: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>Spreadsheets</h2>
              {status?.has_data && (
                <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#666' }}>
                  {status.total_rows} obligations tracked
                </p>
              )}
            </div>
            <span style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              padding: '4px 10px',
              backgroundColor: '#EFF6FF',
              color: '#1E40AF',
              fontSize: '11px',
              fontWeight: '600',
              borderRadius: '6px',
              border: '1px solid #BFDBFE',
            }}>
              💬 Chat Only
            </span>
          </div>
          
          {/* Upload and Refresh buttons */}
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              onClick={loadStatus}
              style={{
                padding: '8px 12px',
                backgroundColor: '#f3f4f6',
                color: '#374151',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
              }}
              title="Refresh spreadsheets list"
            >
              🔄 Refresh
            </button>
            <label style={{
            padding: '8px 16px',
            backgroundColor: '#4ECDC4',
            color: 'white',
            borderRadius: '8px',
            cursor: uploading ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: '500',
            opacity: uploading ? 0.6 : 1,
          }}>
            {uploading ? 'Uploading...' : 'Upload .xlsx'}
            <input
              type="file"
              accept=".xlsx"
              onChange={handleFileUpload}
              disabled={uploading}
              style={{ display: 'none' }}
            />
          </label>
          </div>
        </div>
        
        {/* List of loaded spreadsheets */}
        {status?.spreadsheets && status.spreadsheets.length > 0 && (
          <div style={{
            backgroundColor: '#f9fafb',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '12px 16px',
            marginBottom: '16px',
          }}>
            <div style={{ fontSize: '12px', fontWeight: '600', color: '#6b7280', marginBottom: '8px', textTransform: 'uppercase' }}>
              📊 Loaded Spreadsheets
            </div>
            {status.spreadsheets.map((sheet) => (
              <div key={sheet.id} style={{
                fontSize: '13px',
                color: '#374151',
                padding: '6px 0',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderBottom: status.spreadsheets.length > 1 ? '1px solid #f3f4f6' : 'none',
              }}>
                <span style={{ fontWeight: '500' }}>📄 {sheet.filename}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ color: '#9ca3af', fontSize: '12px' }}>
                    {sheet.row_count} rows • {sheet.tab_count} tab{sheet.tab_count > 1 ? 's' : ''}
                  </span>
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Delete ${sheet.filename}?`)) return;
                      try {
                        await spreadsheetApi.delete(sheet.id);
                        await loadStatus(); // Refresh list
                      } catch (err) {
                        alert(`Failed to delete: ${err.message}`);
                      }
                    }}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: '#ef4444',
                      cursor: 'pointer',
                      fontSize: '12px',
                      padding: '2px 6px',
                    }}
                    title="Delete spreadsheet"
                  >
                    🗑️
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      
      {/* Empty state */}
      {!status?.has_data && !uploading && (
        <div style={{
          textAlign: 'center',
          padding: '60px 20px',
          backgroundColor: '#f9f9f9',
          borderRadius: '12px',
          border: '2px dashed #ddd',
        }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
          <h3 style={{ margin: '0 0 8px 0', fontSize: '16px' }}>No spreadsheet uploaded yet</h3>
          <p style={{ margin: 0, color: '#666', fontSize: '14px' }}>
            Upload a .xlsx file to start tracking obligations
          </p>
        </div>
      )}
      
      {/* Query interface */}
      {status?.has_data && (
        <>
          {/* Preset View Buttons */}
          <div style={{ 
            display: 'flex', 
            gap: '8px', 
            marginBottom: '16px', 
            overflowX: 'auto',
            paddingBottom: '4px',
            WebkitOverflowScrolling: 'touch',
          }}>
            <button
              onClick={() => runPresetQuery("what's overdue?")}
              style={{
                padding: '8px 14px',
                backgroundColor: '#fee2e2',
                color: '#dc2626',
                border: '1px solid #fecaca',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                flex: '0 0 auto',
              }}
            >
              🔴 Overdue
            </button>
            
            <button
              onClick={() => runPresetQuery("what's due today?")}
              style={{
                padding: '8px 14px',
                backgroundColor: '#fef3c7',
                color: '#f59e0b',
                border: '1px solid #fde68a',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                flex: '0 0 auto',
              }}
            >
              🟡 Due Today
            </button>
            
            <button
              onClick={() => runPresetQuery("next 7 days")}
              style={{
                padding: '8px 14px',
                backgroundColor: '#e0f2fe',
                color: '#0ea5e9',
                border: '1px solid #bae6fd',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                flex: '0 0 auto',
              }}
            >
              📅 Next 7 Days
            </button>
            
            <button
              onClick={() => runPresetQuery("next 30 days")}
              style={{
                padding: '8px 14px',
                backgroundColor: '#d1fae5',
                color: '#10b981',
                border: '1px solid #a7f3d0',
                borderRadius: '8px',
                fontSize: '13px',
                fontWeight: '500',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                flex: '0 0 auto',
              }}
            >
              📆 Next 30 Days
            </button>
          </div>
          
          <form onSubmit={handleQuery} style={{ marginBottom: '24px' }}>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input
                type="text"
                className="ov-spreadsheet-query-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Ask about deadlines, expirations, or compliance... (e.g., 'What's due this week?')"
                style={{
                  flex: 1,
                  padding: '12px 16px',
                  border: '1px solid #ddd',
                  borderRadius: '8px',
                  fontSize: '14px',
                }}
                disabled={loading}
              />
              <button
                type="submit"
                className="ov-spreadsheet-query-btn"
                disabled={loading || !query.trim()}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#4ECDC4',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  opacity: (loading || !query.trim()) ? 0.6 : 1,
                }}
              >
                {loading ? '...' : 'Query'}
              </button>
            </div>
            
            {/* Helper text */}
            <p style={{ 
              margin: '8px 0 0 0', 
              fontSize: '12px', 
              color: '#6b7280',
              fontStyle: 'italic'
            }}>
              💡 Spreadsheet queries are text-only. Use voice for document questions.
            </p>
          </form>
          
          {/* Error message */}
          {error && (
            <div style={{
              padding: '12px 16px',
              backgroundColor: '#fee',
              border: '1px solid #fcc',
              borderRadius: '8px',
              color: '#c00',
              fontSize: '14px',
              marginBottom: '16px',
            }}>
              {error}
            </div>
          )}
          
          {/* Results */}
          {results && results.count > 0 && (
            <div>
              {/* Display mode toggle - Desktop only */}
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                marginBottom: '16px',
                flexWrap: 'wrap',
                gap: '12px',
              }}>
                <div style={{ fontSize: '14px', color: '#6b7280', fontWeight: '500' }}>
                  {results.count} {results.count === 1 ? 'item' : 'items'}
                </div>
                
                <div style={{ display: 'flex', gap: '8px' }} className="ov-desktop-only">
                  <button
                    onClick={() => setDisplayMode('compact')}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: displayMode === 'compact' ? '#4ECDC4' : 'white',
                      color: displayMode === 'compact' ? 'white' : '#6b7280',
                      border: '1px solid ' + (displayMode === 'compact' ? '#4ECDC4' : '#e5e7eb'),
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: 'pointer',
                    }}
                  >
                    📋 List
                  </button>
                  
                  <button
                    onClick={() => setDisplayMode('table')}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: displayMode === 'table' ? '#4ECDC4' : 'white',
                      color: displayMode === 'table' ? 'white' : '#6b7280',
                      border: '1px solid ' + (displayMode === 'table' ? '#4ECDC4' : '#e5e7eb'),
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: 'pointer',
                    }}
                  >
                    📊 Table
                  </button>
                  
                  <button
                    onClick={() => setDisplayMode('grouped')}
                    style={{
                      padding: '6px 12px',
                      backgroundColor: displayMode === 'grouped' ? '#4ECDC4' : 'white',
                      color: displayMode === 'grouped' ? 'white' : '#6b7280',
                      border: '1px solid ' + (displayMode === 'grouped' ? '#4ECDC4' : '#e5e7eb'),
                      borderRadius: '6px',
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: 'pointer',
                    }}
                  >
                    👥 Grouped
                  </button>
                </div>
              </div>
              
              {/* Compact List View */}
              {(displayMode === 'compact' || window.innerWidth < 768) && (
                <div>
                  {results.rows.map((row, idx) => renderCompactItem(row, idx))}
                </div>
              )}
              
              {/* Table View - Desktop only */}
              {displayMode === 'table' && window.innerWidth >= 768 && (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{
                    width: '100%',
                    borderCollapse: 'collapse',
                    fontSize: '14px',
                  }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f5f5f5', textAlign: 'left' }}>
                        <th style={{ padding: '10px 12px', borderBottom: '2px solid #ddd' }}>Owner</th>
                        <th style={{ padding: '10px 12px', borderBottom: '2px solid #ddd' }}>Item</th>
                        <th style={{ padding: '10px 12px', borderBottom: '2px solid #ddd' }}>Date</th>
                        <th style={{ padding: '10px 12px', borderBottom: '2px solid #ddd' }}>Days</th>
                        <th style={{ padding: '10px 12px', borderBottom: '2px solid #ddd' }}>Status</th>
                        <th style={{ padding: '10px 12px', borderBottom: '2px solid #ddd' }}>Asset/Unit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.rows.map((row, idx) => (
                        <tr 
                          key={idx} 
                          onClick={() => setSelectedRow(row)}
                          style={{ 
                            borderBottom: '1px solid #eee',
                            cursor: 'pointer',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f9fafb';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <td style={{ padding: '10px 12px' }}>{row.owner || '—'}</td>
                          <td style={{ padding: '10px 12px' }}>{row.item || '—'}</td>
                          <td style={{ padding: '10px 12px' }}>
                            {row.event_date ? new Date(row.event_date).toLocaleDateString() : '—'}
                          </td>
                          <td style={{ padding: '10px 12px' }}>
                            {row.days_until !== null ? (
                              row.days_until >= 0 ? `${row.days_until}` : `${Math.abs(row.days_until)} ago`
                            ) : '—'}
                          </td>
                          <td style={{ padding: '10px 12px' }}>
                            {getStatusBadge(row.status)}
                          </td>
                          <td style={{ padding: '10px 12px', color: '#666' }}>
                            {row.asset_unit || '—'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              {/* Grouped View */}
              {displayMode === 'grouped' && (
                <div>
                  {/* Group by selector */}
                  <div style={{ marginBottom: '16px', display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: '500' }}>Group by:</span>
                    <select 
                      value={groupBy}
                      onChange={(e) => setGroupBy(e.target.value)}
                      style={{
                        padding: '6px 10px',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        fontSize: '13px',
                        backgroundColor: 'white',
                      }}
                    >
                      <option value="owner">Owner</option>
                      <option value="item">Item</option>
                      <option value="asset_unit">Asset/Unit</option>
                    </select>
                  </div>
                  
                  {/* Grouped items */}
                  {Object.entries(groupResults(results.rows, groupBy)).map(([groupKey, groupRows]) => (
                    <details key={groupKey} open style={{ marginBottom: '16px' }}>
                      <summary style={{
                        padding: '12px 16px',
                        backgroundColor: '#f9fafb',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '14px',
                        color: '#374151',
                        border: '1px solid #e5e7eb',
                      }}>
                        {groupKey} ({groupRows.length})
                      </summary>
                      <div style={{ marginTop: '8px', paddingLeft: '8px' }}>
                        {groupRows.map((row, idx) => renderCompactItem(row, `${groupKey}-${idx}`))}
                      </div>
                    </details>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {results && results.count === 0 && (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: '#666',
              fontSize: '14px',
            }}>
              No results found. Try a different query.
            </div>
          )}
        </>
      )}
      
      {/* Row Detail Drawer */}
      {selectedRow && renderDrawer()}
      
      {/* Mobile-responsive styles */}
      <style>{`
        @media (min-width: 768px) {
          .ov-drawer-mobile {
            right: auto !important;
            left: auto !important;
            bottom: auto !important;
            top: 0 !important;
            right: 0 !important;
            width: 400px !important;
            max-height: 100vh !important;
            border-radius: 0 !important;
            animation: slideInRight 0.3s ease-out !important;
          }
          .ov-desktop-only {
            display: flex !important;
          }
        }
        
        @media (max-width: 767px) {
          .ov-desktop-only {
            display: none !important;
          }
        }
        
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        
        @keyframes slideUp {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        
        @keyframes slideInRight {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
      `}</style>
    </div>
  );
};

export default SpreadsheetPanel;
